// The unsubscribe control for one sender: where they stand on the ladder, the next rung worth
// trying, and a plain-language confirmation whose server preview fingerprint arms
// the POST; a separate Confirm sends it. Mailto opens the compose sheet with the server's draft;
// link opens a new tab after the server recorded it. No single key press starts any rung. After
// a rung, "Screen out this sender" is offered through the existing screen action, never made
// on its own.
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, ShieldCheck } from "lucide-react";
import type { SenderView, UnsubscribePreview, UnsubscribeRung, UnsubscribeView } from "@shared/api";
import { cn } from "@web/lib/cn";
import { useUnsubscribePreview, useUnsubscribeView } from "@web/lib/unsubscribeQueries";
import { formatAddress } from "@web/lib/addresses";
import { formatShortDateTime } from "@web/lib/time";
import { Row } from "@web/feed/card/ApproveStrip";
import { workPhaseLabel } from "@web/feed/wording";
import { screenSender } from "@web/screener/screenOps";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";
import { WorkControls } from "@web/work/WorkControls";
import {
  ladderDetail,
  ladderTone,
  offersScreenOut,
  otherRungs,
  rungHint,
  rungLabel,
  showsLadder,
  unsubscribeStateLabel,
} from "./unsubscribe";
import { climbRung } from "./unsubscribeOps";

/** A second press within this window is a double-tap, not a confirmation. */
const CONFIRM_GUARD_MS = 250;

/** The minimal sender the screen action needs when only the ladder view is at hand. */
function senderFor(view: UnsubscribeView, sender: SenderView | null | undefined): SenderView {
  return (
    sender ?? {
      accountId: view.accountId,
      address: view.address,
      domain: view.address.slice(view.address.lastIndexOf("@") + 1),
      name: view.name,
      inboundN: 1,
      outboundN: 0,
      lastInAt: null,
      lastOutAt: null,
      avgReplyChars: null,
      isList: true,
      screen: view.screen,
      screenedAt: null,
      firstSeenAt: null,
      awaitingDecision: false,
      unsubscribe: view.state,
    }
  );
}

function OneClickPanel({
  view,
  busy,
  onConfirm,
  onCancel,
  showTitle = true,
}: {
  view: UnsubscribeView;
  busy: boolean;
  onConfirm: (fingerprint: string) => void;
  onCancel: () => void;
  showTitle?: boolean;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  const armedAt = useRef(0);
  const preview = useUnsubscribePreview(view.accountId, view.address, view.lastMessageKey, true);
  const p = preview.data ?? null;
  useEffect(() => {
    armedAt.current = Date.now();
  }, []);
  // Confirm can only be focused once the exact address is on screen (it is disabled before).
  useEffect(() => {
    if (p && !preview.isFetching) confirmRef.current?.focus({ preventScroll: true });
  }, [p, preview.isFetching]);
  const confirm = () => {
    if (Date.now() - armedAt.current < CONFIRM_GUARD_MS) return;
    if (p && !preview.isFetching && !preview.isError) onConfirm(p.fingerprint);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    }
  };
  return (
    <div
      role="group"
      aria-label="Confirm unsubscribe"
      data-testid="unsubscribe-panel"
      onKeyDown={onKeyDown}
      className="flex flex-col gap-4"
    >
      {showTitle ? (
        <p className="flex items-center gap-2 font-medium text-ink">
          <ShieldCheck aria-hidden className="size-4 text-accent-text" />
          Unsubscribe from {view.name || view.address}?
        </p>
      ) : null}
      <p className="text-sm text-muted">
        Stop subscription mail from this sender. Existing emails stay where they are.
      </p>
      <p className="text-sm text-muted" aria-live="polite">
        {p ? (
          <>
            Request goes to <strong className="text-ink">{p.host}</strong>.
          </>
        ) : preview.isError ? (
          <>
            Couldn’t check the unsubscribe address. Nothing was sent.{" "}
            <Button size="sm" onClick={() => void preview.refetch()}>
              Try again
            </Button>
          </>
        ) : (
          "Checking the unsubscribe address…"
        )}
      </p>
      {p ? (
        <details className="text-xs text-muted">
          <summary className="cursor-pointer">Request details</summary>
          <dl className="mt-2 flex flex-col gap-2">
            <Row label="Sender">
              {formatAddress({ email: view.address, name: view.name ?? undefined })}
            </Row>
            <Row label="Address">
              <code className="break-all" data-testid="unsubscribe-url">
                {p.url}
              </code>
            </Row>
          </dl>
        </details>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted">
          <Kbd>Enter</Kbd> to confirm · <Kbd>Esc</Kbd> to cancel
        </p>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button
            ref={confirmRef}
            size="sm"
            variant="primary"
            busy={busy}
            disabled={!p || preview.isFetching || preview.isError}
            onKeyDown={(e) => {
              if (e.repeat) e.preventDefault();
            }}
            onClick={confirm}
            data-testid="unsubscribe-confirm"
          >
            Confirm unsubscribe
          </Button>
        </div>
      </div>
    </div>
  );
}

export function UnsubscribeControl({
  accountId,
  address,
  sender,
  compact = false,
  className,
  showUnavailable = false,
  onDraftOpened,
  confirmOnOpen = false,
  onCancel,
}: {
  accountId: string;
  address: string;
  /** The sender row when at hand (the screen action needs it; the view stands in otherwise). */
  sender?: SenderView | null;
  /** A one-line form for list rows: the state and the suggested rung only. */
  compact?: boolean;
  className?: string;
  showUnavailable?: boolean;
  onDraftOpened?: () => void;
  /** Opening the email dialog previews the supported method, without sending anything. */
  confirmOnOpen?: boolean;
  onCancel?: () => void;
}) {
  const queryClient = useQueryClient();
  const query = useUnsubscribeView(accountId, address);
  const [armed, setArmed] = useState(confirmOnOpen);
  const [busy, setBusy] = useState(false);
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);
  const view = query.data ?? null;
  if (!view) {
    if (!showUnavailable) return null;
    return (
      <div role="status" className="text-sm text-muted">
        {query.isPending ? (
          "Checking unsubscribe options…"
        ) : query.isError ? (
          <>
            Couldn’t check unsubscribe options.{" "}
            <Button size="sm" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </>
        ) : (
          "No unsubscribe options found in the cached headers. Read the email and look for a subscription preferences link in its footer."
        )}
      </div>
    );
  }
  if (!showUnavailable && !showsLadder(sender ?? { isList: false }, view)) return null;

  const status = view.state;
  const tone = ladderTone(status);
  const detail = ladderDetail(view);
  const suggested = view.suggestedRung;
  const others = compact ? [] : otherRungs(view);
  const confirming =
    armed && suggested === "one_click" && status.state !== "requested" && status.state !== "done";
  const work = view.work;
  const inFlight = status.state === "requested" && status.rung === "one_click" && work;
  const failedWork =
    work && (work.status === "blocked" || work.status === "failed") && status.state !== "done";

  const start = async (rung: UnsubscribeRung) => {
    if (busy) return;
    if (rung === "one_click") {
      setArmed(true);
      return;
    }
    setArmed(false); // Choosing another method leaves the one-click confirmation.
    setBusy(true);
    try {
      const out = await climbRung(queryClient, view, rung);
      setBlockedUrl(out?.blockedUrl ?? null);
      if (out?.result.kind === "draft") onDraftOpened?.();
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (fingerprint: UnsubscribePreview["fingerprint"]) => {
    if (busy) return;
    setBusy(true);
    try {
      const out = await climbRung(queryClient, view, "one_click", fingerprint);
      if (out) setArmed(false);
    } finally {
      setBusy(false);
    }
  };

  const screenOut = () => void screenSender(queryClient, senderFor(view, sender), "screened_out");

  // Reachable both in the ladder row and beside an armed one-click confirmation, so a dialog
  // that opens armed still lets the owner choose the email or website method instead.
  const otherWays = others.length ? (
    <details className="text-sm text-muted" data-testid="unsubscribe-other-ways">
      <summary className="cursor-pointer px-2 py-1">Other ways</summary>
      <div className="mt-1 flex flex-wrap gap-1">
        {others.map((r) => (
          <Button
            key={r}
            size="sm"
            variant="ghost"
            disabled={busy}
            title={rungHint(r, view.methods)}
            onClick={() => void start(r)}
            data-testid={`unsubscribe-rung-${r}`}
          >
            {rungLabel(r)}
            {r === "link" ? <ExternalLink aria-hidden className="size-3.5" /> : null}
          </Button>
        ))}
      </div>
    </details>
  ) : null;

  return (
    <div
      className={cn("flex flex-col gap-2", className)}
      data-testid="unsubscribe-control"
      data-state={status.state}
    >
      {confirmOnOpen && !confirming && suggested && suggested !== "one_click" ? (
        <p className="text-sm text-muted">
          {suggested === "mailto"
            ? "This sender asks you to unsubscribe by email. Review the prepared draft, then send it."
            : "Finish unsubscribing on this sender’s website."}
        </p>
      ) : null}
      {!confirming ? (
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="text-muted">Unsubscribe:</span>
          <span
            className={cn(
              "min-w-0 font-medium",
              tone === "calm" ? "text-calm" : tone === "note" ? "text-note-ink" : "text-ink",
            )}
            data-testid="unsubscribe-state"
          >
            {unsubscribeStateLabel(status)}
          </span>
          {detail && !compact ? <span className="min-w-0 text-muted">· {detail}</span> : null}
          {status.at && !compact ? (
            <span className="text-xs text-muted">· {formatShortDateTime(status.at)}</span>
          ) : null}
          <span className="ml-auto flex max-w-full min-w-0 flex-wrap items-center gap-1">
            {suggested ? (
              <Button
                size="sm"
                variant={status.state === "none" ? "secondary" : "ghost"}
                disabled={busy}
                title={rungHint(suggested, view.methods)}
                onClick={() => void start(suggested)}
                data-testid={`unsubscribe-rung-${suggested}`}
              >
                {status.state === "failed" || status.state === "unknown" ? "Next: " : ""}
                {suggested === "one_click"
                  ? "Unsubscribe"
                  : suggested === "mailto"
                    ? "Prepare unsubscribe email"
                    : "Open unsubscribe page"}
                {suggested === "link" ? <ExternalLink aria-hidden className="size-3.5" /> : null}
              </Button>
            ) : null}
            {otherWays}
            {offersScreenOut(view) ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={screenOut}
                data-testid="unsubscribe-screen-out"
              >
                Screen out this sender
              </Button>
            ) : null}
          </span>
        </div>
      ) : null}
      {!view.methods.oneClick && !view.methods.mailto && !view.methods.link ? (
        <p className="text-sm text-muted">
          No unsubscribe options found in the cached headers. Read the email and look for a
          subscription preferences link in its footer.
        </p>
      ) : null}
      {confirming ? (
        <OneClickPanel
          view={view}
          busy={busy}
          onConfirm={(fingerprint) => void confirm(fingerprint)}
          showTitle={!confirmOnOpen}
          onCancel={() => {
            setArmed(false);
            onCancel?.();
          }}
        />
      ) : null}
      {confirming ? otherWays : null}
      {blockedUrl ? (
        <p className="text-sm text-muted">
          Open it yourself:{" "}
          <a
            href={blockedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent-text underline underline-offset-2"
          >
            {view.methods.link?.host ?? "their page"}
          </a>
        </p>
      ) : null}
      {!compact && (inFlight || failedWork) && work ? (
        <div
          className={cn(
            "flex flex-col gap-2 rounded-lg px-3 py-2 text-sm ring-1",
            failedWork ? "bg-note/60 ring-note-line" : "bg-paper ring-line",
          )}
        >
          {failedWork ? (
            <p className="text-note-ink">
              Try again sends the same request once more, after checking the address again.
            </p>
          ) : (
            <p className="font-medium text-ink">{workPhaseLabel(work)}</p>
          )}
          <WorkControls
            work={{ ...work, recovery: work.recovery.filter((r) => r !== "edit") }}
            compact
          />
        </div>
      ) : null}
    </div>
  );
}
